import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { ImportLog } from "../../types/core/ImportLog";
import { unwrappedDispatch } from "../../foundation/Dispatching";
import Student from "../../types/core/Student";
import Contact from "../../types/core/Contact";
import { saveContacts } from "./Contact";

// Define the type for our state store entry
type StudentState = {
    archives: Student[];
    students: Student[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: StudentState = {
    archives: [],
    students: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const studentSlice = createSlice({
    name: "student",
    initialState: initialState,
    reducers: {
        resetStudentStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    archives:
                        rawData?.payload?.student?.archives?.map(c => new Student(c)) ||
                        [],
                    students:
                        rawData?.payload?.student?.students?.map(c => new Student(c)) ||
                        [],
                    status: !rawData?.payload?.student?.students ? "idle" : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchStudents.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchStudents.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.students = action.payload[0].map(c => new Student(c));
                state.archives = action.payload[1].map(c => new Student(c));
            })
            .addCase(fetchStudents.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addStudent.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addStudent.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.students = state.students.concat(
                    action.payload.map(c => new Student(c)),
                );
            })
            .addCase(addStudent.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateStudent.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateStudent.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const studentIds = action.payload.map(c => c.id);
                state.students = state.students
                    .filter(c => !studentIds.includes(c.id))
                    .concat(action.payload.map(c => new Student(c)));
            })
            .addCase(updateStudent.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(archiveStudent.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(archiveStudent.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const studentIds = action.payload.map(c => c.id);
                state.students = state.students.filter(
                    c => !studentIds.includes(c.id),
                );
                state.archives = state.archives.concat(
                    action.payload.map(c => new Student(c)),
                );
            })
            .addCase(archiveStudent.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetStudentStatus } = studentSlice.actions;

export const initialiseStudentDetails = createAsyncThunk(
    "student/initialiseStudentDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchStudents()),
);

// This is a thunk, they are used to load the state
export const fetchStudents = createAsyncThunk(
    "student/fetchStudents",
    () =>
        Promise.all([
            Datastore.getType<Student[]>("api/Students"),
            Datastore.getType<Student[]>("api/Students/Archive"),
        ]),
    {
        condition: (_, api) =>
            store.getState().db.student.status === "idle",
    },
);
export const addStudent = createAsyncThunk(
    "student/addStudent",
    async (payload: Student[]) => {
        const temp = new Map<Student, Contact[]>();
        for (const student of payload) {
            temp.set(student, student.contacts ?? []);
            delete student.contacts;
        }
        const waitContacts = Array.from(temp.values()).flat();

        const students = await Datastore.postType<Student[]>(
            "api/Students",
            payload,
        );

        if (waitContacts.length) {
            const transmitContacts: Contact[] = [];
            const lookup = Array.from(temp.entries());
            for (const contact of waitContacts) {
                const sendStudent = lookup.find(([_, contacts]) =>
                    contacts.includes(contact),
                )?.[0];
                if (!sendStudent) continue;
                const responseStudent = students.find(
                    s => s.code === sendStudent.code,
                );
                if (!responseStudent) continue;
                contact.ownerId = responseStudent.personID;
                transmitContacts.push(contact);
            }
            await saveContacts(transmitContacts);
        }

        return students;
    },
    {
        condition: (_, api) =>
            store.getState().db.student.status === "succeeded",
    },
);
export const updateStudent = createAsyncThunk(
    "student/updateStudent",
    async (payload: Student[]) => {
        const temp = new Map<Student, Contact[]>();
        for (const student of payload) {
            for (const contact of student.contacts ?? [])
                contact.ownerId = student.personID;
            temp.set(student, student.contacts ?? []);
            delete student.contacts;
        }
        const transmitContacts = Array.from(temp.values()).flat();
        const waits = [];

        waits.push(Datastore.putType<Student[]>("api/Students", payload));
        waits.push(saveContacts(transmitContacts));

        const [students] = await Promise.all(waits);

        return students;
    },
    {
        condition: (_, api) =>
            store.getState().db.student.status === "succeeded",
    },
);
export const archiveStudent = createAsyncThunk(
    "student/archiveStudent",
    (payload: Student[]) => {
        Datastore.post(
            "api/Students/Archive",
            payload.map(c => c.id),
        );
        return payload;
    },
    {
        condition: (_, api) =>
            store.getState().db.student.status === "succeeded",
    },
);

/*******     Importing     *******/
export const importStudentsFromTD = async (id: number) => {
    const result = await Datastore.postType<ImportLog>(
        `api/Students/Import/TD/${id}`,
    );

    store.dispatch(resetStudentStatus());
    await unwrappedDispatch(fetchStudents());

    return result;
};

// We have to export the reducer as this is added to the state store
export default studentSlice.reducer;
